/// <amd-module name="Core/Medius.Core.Web/Scripts/components/taskHandling/shared/modals/rejectManagementWizard/rejectManagementWizard"/>
import * as React from "react";
import { Button, ButtonGroup, DialogPart, FlexLayout, FlexSpacer, InstantSearchBox, LocalizationContext, ModalDialogShell, StringMap, Text } from "@medius/ui-controls";
import { translate } from "Core/Medius.Core.Web/Scripts/lib/globalization";
import RejectManagementWizardGrid from "./rejectManagementWizardGrid";
import { postISupplierInvoiceRejectionDto, getSelectedReasonCodes } from "./services";
import { IRejectManagementReasonCode, ISupplierInvoiceRejectionDto, RejectManagementWizardType } from "./interfaces";
import { handleError } from "Core/Medius.Core.Web/Scripts/lib/errorHandling/errorHandler";
import { RejectManagementEmailWizard } from "./rejectManagementEmailWizard";
import { useInvoiceReasonCodeData } from "./useInvoiceReasonCodeData";
import { mapFromNorthstarToApa } from "Core/Medius.Core.Web/Scripts/lib/northstarLocalization";
import { getCultureCode } from "Core/Medius.Core.Web/Scripts/Medius/lib/utils/user";
import { useRejectManagementEmailForm } from "./useRejectManagementEmailForm";
import { FieldValues } from "react-hook-form";
import { ReclassifyTypeModal } from "./reclassifyTypeModal";
import { isEmailSendingInRejectionManagementEnabled } from "Core/Medius.Core.Web/Scripts/Medius/core/featureToggle";

export interface RejectManagementWizardProps {
    wizardType: RejectManagementWizardType;
    documentId: number;
    hierarchyId: string;
    onClose: (wasConfirmed?: boolean, targetDocumentType?: string) => void;
    action: () => void;
    possibleReclassifyTypesList?: string[];
    currentDocumentType?: string;
}

enum RejectManagementWizardStep {
    ReasonCode,
    Email,
    ReclassifyType
}

export const RejectManagementWizard: React.FC<RejectManagementWizardProps> = ({ wizardType, documentId, hierarchyId, onClose, possibleReclassifyTypesList, currentDocumentType/*, action*/ }) => {
    const searchLabelId = "searchLabelId";
    const [selection, setSelection] = React.useState<IRejectManagementReasonCode[]>([]);
    const [step, setStep] = React.useState<RejectManagementWizardStep>(RejectManagementWizardStep.ReasonCode);
    const [targetDocumentType, setTargetDocumentType] = React.useState<string>(null);
    const formContext = useRejectManagementEmailForm();
    const { handleSubmit } = formContext;

    const getWizardTitle = React.useCallback((): string => {
        switch (wizardType) {
            case RejectManagementWizardType.Delete:
                return translate("#PurchaseToPay/documentReasonCodes/deleteInvoice");
            case RejectManagementWizardType.Invalidate:
                return translate("#PurchaseToPay/documentReasonCodes/invalidateInvoice");
            case RejectManagementWizardType.InvalidateAndEdit:
                return translate("#PurchaseToPay/documentReasonCodes/invalidateAndEditInvoice");
        }
    }, [wizardType]);

    const hasExternalType = selection.some(rc => Array.isArray(rc.types) && rc.types.includes("External"));

    const getButtonLabel = React.useCallback((): string => {
        if (step === RejectManagementWizardStep.Email) {
            switch (wizardType) {
                case RejectManagementWizardType.Delete:
                    return translate("#Core/deleteIt");
                case RejectManagementWizardType.Invalidate:
                    return translate("#Core/invalidateIt");
                case RejectManagementWizardType.InvalidateAndEdit:
                    return translate("#Core/next");
            }
        }
        if(step === RejectManagementWizardStep.ReclassifyType){
            return translate("#Core/registerDocument");
        }
        if (hasExternalType && isEmailSendingInRejectionManagementEnabled()) return translate("#Core/next");
        switch (wizardType) {
            case RejectManagementWizardType.Delete:
                return translate("#Core/deleteIt");
            case RejectManagementWizardType.Invalidate:
            case RejectManagementWizardType.InvalidateAndEdit:
                return translate("#Core/invalidateIt");
        }
    }, [wizardType, hasExternalType, step]);

    const getModalTitle = React.useCallback((): string => {
        if (step === RejectManagementWizardStep.Email) {
            return translate("#PurchaseToPay/documentReasonCodes/rejectManagementWizard/sendEmailToSupplier");
        }
        if (step === RejectManagementWizardStep.ReclassifyType) {
            return translate("#Core/chooseReclassifyDocumentType");
        }
        return translate("#PurchaseToPay/documentReasonCodes/rejectManagementWizard/selectReasonCode");
    }, [step]);

    const invoiceReasonCodeData = useInvoiceReasonCodeData(wizardType, hierarchyId);
    const { searching: { changeSearchTerm: setSearch } } = invoiceReasonCodeData;
    const isDeleteButtonDisabled = invoiceReasonCodeData.forceReasonCode && selection?.length === 0;

    const executeRejectionFlow = async (formData: FieldValues) => {
        try {
            const sendEmailInformation = step === RejectManagementWizardStep.Email ? formData.sendToSupplier : false;
            
            const supplierInvoiceRejectionDto: ISupplierInvoiceRejectionDto = {
                documentId: documentId,
                invoiceRejectionReasonItems: selection.map(rc => ({
                    reasonCodeId: rc.id,
                    actionId: rc.rejectionActionId
                })),
                sendToSupplier: sendEmailInformation,
                ...(sendEmailInformation && {
                    invoiceRejectionEmail: {
                        emailTo: formData.emailTo,
                        sendCopy: formData.sendCopy,
                        messageSubject: formData.messageSubject,
                        content: formData.content
                    }
                })
            };

            await postISupplierInvoiceRejectionDto(supplierInvoiceRejectionDto);
            // Do not uncomment this until the story (MFD-58517) for sending events to CRFS is completed.
            // action();
            //notification.success(translate("#PurchaseToPay/documentReasonCodes/rejectManagementWizard/invoiceDeleted"));
            onClose(true, targetDocumentType); // confirmed
        } catch (e) {
            handleError(e);
        }
    };

    const handleDelete = handleSubmit(async (formData: FieldValues) => {

        if (step === RejectManagementWizardStep.ReclassifyType){
            await executeRejectionFlow(formData);
            return;
        }

        if (step === RejectManagementWizardStep.Email) {
            if(wizardType === RejectManagementWizardType.InvalidateAndEdit)
                setStep(RejectManagementWizardStep.ReclassifyType);
            else
                await executeRejectionFlow(formData);
            return;
        }

        if (hasExternalType && isEmailSendingInRejectionManagementEnabled()) {
            setStep(RejectManagementWizardStep.Email);
            return;
        }

        if(wizardType === RejectManagementWizardType.InvalidateAndEdit)
        {
            setStep(RejectManagementWizardStep.ReclassifyType);
            return;
        }

        await executeRejectionFlow(formData);
    });

    const calculateBackState = () : RejectManagementWizardStep =>{
        if(step === RejectManagementWizardStep.ReclassifyType){
            if(hasExternalType)
                return RejectManagementWizardStep.Email;
        }
        return RejectManagementWizardStep.ReasonCode;
    };

    React.useEffect(() => {
        const loadSelectedReasonCodes = async () => {
            const selectedReasonCodes = await getSelectedReasonCodes(documentId);
            setSelection(selectedReasonCodes);
        };

        loadSelectedReasonCodes();
    }, []);
    
    React.useEffect(() => {
        if(currentDocumentType)
            setTargetDocumentType(currentDocumentType);
    }, [currentDocumentType]);
    
    return (
        <LocalizationContext.Provider
            value={{
                translate: (key: string, _, defaultValue?: string, args?: StringMap) =>
                    mapFromNorthstarToApa(key, defaultValue, args),
                getLocale: () => getCultureCode()
            }}
        >
            <ModalDialogShell
                isOpen
                hasHeader
                height="content"
                title={getWizardTitle()}
                data-testid="reject-management-wizard"
                onRequestClose={() => onClose(false)} >
                {dialogProps => <>
                    <DialogPart.Title {...dialogProps.headerProps} />
                    <DialogPart.Content width="large">
                        <div className="reject-management-wizard">
                            <div className="reject-management-wizard__header">
                                <Text id={searchLabelId} variant="heading3">{getModalTitle()}</Text>
                                {step === RejectManagementWizardStep.ReasonCode && (
                                    <InstantSearchBox
                                        data-testid="reject-management-wizard-search-box"
                                        labelId={searchLabelId}
                                        width="fill"
                                        spacerMode="on"
                                        executeSearch={(searchTerm) => { setSearch(searchTerm); }} />
                                )}
                            </div>
                            <div className="reject-management-wizard__content">
                                {step === RejectManagementWizardStep.Email && (
                                    <RejectManagementEmailWizard
                                        formContext={formContext} 
                                        documentType={currentDocumentType}
                                        documentId={documentId}/>)}
                                {step === RejectManagementWizardStep.ReasonCode && (
                                    <RejectManagementWizardGrid
                                        selection={selection}
                                        setSelection={setSelection}
                                        invoiceReasonCodeData={invoiceReasonCodeData}
                                    />
                                )}
                                {step === RejectManagementWizardStep.ReclassifyType && (
                                    <ReclassifyTypeModal
                                        possibleReclassifyTypeList={possibleReclassifyTypesList} 
                                        targetDocumentType={targetDocumentType}
                                        setTargetDocumentType={setTargetDocumentType}
                                    />
                                )}
                           </div>                         
                        </div>
                    </DialogPart.Content>
                    <DialogPart.Footer>
                        <FlexLayout>
                            {(step !== RejectManagementWizardStep.ReasonCode) && (
                                <Button
                                    data-testid="reject-wizard-back-button"
                                    label={translate("#Core/back")}
                                    onClick={() =>
                                        setStep(calculateBackState())
                                    }
                                    variant="inline"
                                />
                            )}
                            <FlexSpacer />
                            <ButtonGroup>
                                <Button
                                    data-testid="reject-wizard-cancel-button"
                                    label={translate("#Core/cancel")}
                                    variant="secondary"
                                    onClick={() => onClose(false)} />
                                <Button
                                    data-testid="reject-wizard-delete-it-button"
                                    label={getButtonLabel()}
                                    onClick={handleDelete} 
                                    disabled={isDeleteButtonDisabled}
                                    />
                            </ButtonGroup>
                        </FlexLayout>
                    </DialogPart.Footer>
                </>}
            </ModalDialogShell>
        </LocalizationContext.Provider>
    );
};